import { z } from "zod";

import { DEFAULT_LAYOUT_TEMPLATE } from "./defaultLayout.template";
import type { GroupId, WorkspaceSnapshot } from "./workspace.types";
import {
  WORKSPACE_TEMPLATE_ID_VALUES,
  type WorkspaceTemplateId,
} from "./workspaceTemplates";

const workspaceTemplateIdSchema = z.enum(WORKSPACE_TEMPLATE_ID_VALUES);

const placementSchema = z
  .object({
    widgetId: z.string().min(1),
    x: z.number().finite(),
    y: z.number().finite(),
    w: z.number().finite(),
    h: z.number().finite(),
    minW: z.number().finite().optional(),
    minH: z.number().finite().optional(),
    maxW: z.number().finite().optional(),
    maxH: z.number().finite().optional(),
    fixedWidthPx: z.number().finite().positive().optional(),
    minWidthPx: z.number().finite().positive().optional(),
    alignX: z.enum(["end"]).optional(),
  })
  .strict();

const widgetSchema = z
  .object({
    id: z.string().min(1),
    kind: z.enum([
      "watchlist",
      "chart",
      "portfolioHistory",
      "positions",
      "orders",
      "orderTicket",
      "optionsChain",
      "balances",
      "activities",
    ]),
    groupId: z
      .enum([
        "A",
        "B",
        "C",
        "D",
        "E",
        "F",
        "G",
        "H",
        "I",
        "J",
        "K",
        "L",
        "M",
        "N",
        "O",
        "P",
        "Q",
        "R",
        "S",
        "T",
        "U",
        "V",
        "W",
        "X",
        "Y",
        "Z",
      ])
      .optional(),
    localSymbol: z.string().min(1).optional(),
    orderTicketPrefs: z
      .object({
        reviewEnabled: z.boolean(),
        reviewPresentation: z.enum(["inline", "modal"]),
      })
      .strict()
      .optional(),
    watchlistId: z.string().uuid().optional(),
  })
  .strict();

const portfolioHistoryWidgetSettingsSchema = z
  .object({
    kind: z.literal("portfolioHistory"),
    comparisons: z.array(
      z
        .object({
          color: z.enum(["blue", "green", "orange", "purple", "pink", "teal"]),
          symbol: z.string().min(1),
        })
        .strict(),
    ),
  })
  .strict();

const chartWidgetSettingsSchema = z
  .object({
    kind: z.literal("chart"),
    state: z.record(z.string(), z.unknown()),
    comparisonSymbols: z.array(z.string().min(1)).optional(),
    comparisonSymbol: z.string().min(1).optional(),
    showOpenLimitOrders: z.boolean().optional(),
    showHistoricalOrderFills: z.boolean().optional(),
  })
  .strict();

const widgetSettingsSchema = z.record(
  z.string(),
  z.discriminatedUnion("kind", [
    portfolioHistoryWidgetSettingsSchema,
    chartWidgetSettingsSchema,
  ]),
);

const workspaceSnapshotSchema = z
  .object({
    engine: z.literal("rgl"),
    placements: z.array(placementSchema),
    widgets: z.array(widgetSchema),
    widgetSettings: widgetSettingsSchema.default({}),
  })
  .strict()
  .superRefine((workspace, context) => {
    const widgetIds = workspace.widgets.map(({ id }) => id);
    const placementIds = workspace.placements.map(({ widgetId }) => widgetId);
    const addDuplicateIssue = (
      ids: ReadonlyArray<string>,
      path: "widgets" | "placements",
    ) => {
      if (new Set(ids).size !== ids.length) {
        context.addIssue({
          code: "custom",
          message: `Duplicate ${path} ids`,
          path: [path],
        });
      }
    };
    addDuplicateIssue(widgetIds, "widgets");
    addDuplicateIssue(placementIds, "placements");

    const widgetSet = new Set(widgetIds);
    const placementSet = new Set(placementIds);
    if (
      widgetSet.size !== placementSet.size ||
      [...widgetSet].some((id) => !placementSet.has(id))
    ) {
      context.addIssue({
        code: "custom",
        message: "Widget and placement ids must match",
        path: ["placements"],
      });
    }

    const widgetsById = new Map(
      workspace.widgets.map((widget) => [widget.id, widget]),
    );
    for (const [widgetId, settings] of Object.entries(
      workspace.widgetSettings,
    )) {
      const widget = widgetsById.get(widgetId);
      if (!widget || widget.kind !== settings.kind) {
        context.addIssue({
          code: "custom",
          message: "Widget settings must reference a widget of the same kind",
          path: ["widgetSettings", widgetId],
        });
      }
    }
  });

const workspaceEntrySchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1).max(100),
    templateId: workspaceTemplateIdSchema.optional(),
    workspace: workspaceSnapshotSchema,
    settings: z.record(z.string(), z.unknown()),
  })
  .strict();

const workspaceDocumentSchema = z
  .object({
    schemaVersion: z.literal(3),
    revision: z.number().int().nonnegative(),
    engine: z.literal("rgl"),
    workspaces: z.array(workspaceEntrySchema),
  })
  .strict();

export interface WorkspaceEntry {
  id: string;
  name: string;
  templateId?: WorkspaceTemplateId;
  workspace: WorkspaceSnapshot;
  settings: Record<string, unknown>;
}

export interface WorkspaceDocument {
  schemaVersion: 3;
  revision: number;
  engine: "rgl";
  workspaces: WorkspaceEntry[];
}

export const EMPTY_WORKSPACE_SNAPSHOT: WorkspaceSnapshot = {
  engine: "rgl",
  widgets: [],
  placements: [],
  widgetSettings: {},
};

const remapWidgetSettings = (
  widgetSettings: WorkspaceSnapshot["widgetSettings"],
  remapWidgetId: (widgetId: string) => string,
): WorkspaceSnapshot["widgetSettings"] =>
  Object.fromEntries(
    Object.entries(widgetSettings).map(([widgetId, settings]) => [
      remapWidgetId(widgetId),
      structuredClone(settings),
    ]),
  );

export const createWorkspaceEntry = (
  name: string,
  options: {
    id?: string;
    templateId?: WorkspaceTemplateId;
    workspace?: WorkspaceSnapshot;
    settings?: Record<string, unknown>;
  } = {},
): WorkspaceEntry => ({
  id: options.id ?? crypto.randomUUID(),
  name,
  ...(options.templateId === undefined
    ? {}
    : { templateId: options.templateId }),
  workspace: structuredClone(options.workspace ?? EMPTY_WORKSPACE_SNAPSHOT),
  settings: structuredClone(options.settings ?? {}),
});

export const withWorkspaceGroup = (
  workspace: WorkspaceSnapshot,
  groupId: GroupId,
): WorkspaceSnapshot => ({
  ...workspace,
  widgets: workspace.widgets.map((widget) => ({ ...widget, groupId })),
});

export const instantiateWorkspaceEntry = (
  name: string,
  workspace: WorkspaceSnapshot,
  settings?: Record<string, unknown>,
  templateId?: WorkspaceTemplateId,
): WorkspaceEntry => {
  const cloned = structuredClone(workspace);
  const widgetIds = new Map(
    cloned.widgets.map((widget) => [widget.id, crypto.randomUUID()]),
  );
  const remapWidgetId = (widgetId: string) => {
    const remapped = widgetIds.get(widgetId);
    if (!remapped) {
      throw new Error(`Unknown widget id: ${widgetId}`);
    }
    return remapped;
  };
  return createWorkspaceEntry(name, {
    workspace: {
      ...cloned,
      widgets: cloned.widgets.map((widget) => ({
        ...widget,
        id: remapWidgetId(widget.id),
      })),
      placements: cloned.placements.map((placement) => ({
        ...placement,
        widgetId: remapWidgetId(placement.widgetId),
      })),
      widgetSettings: remapWidgetSettings(cloned.widgetSettings, remapWidgetId),
    },
    settings,
    templateId,
  });
};

export const duplicateWorkspaceEntry = (
  source: WorkspaceEntry,
  name: string,
): WorkspaceEntry => {
  const workspace = structuredClone(source.workspace);
  const widgetIds = new Map(
    workspace.widgets.map((widget) => [widget.id, crypto.randomUUID()]),
  );
  const remapWidgetId = (widgetId: string) => {
    const remapped = widgetIds.get(widgetId);
    if (!remapped) {
      throw new Error(`Unknown widget id: ${widgetId}`);
    }
    return remapped;
  };
  return createWorkspaceEntry(name, {
    workspace: {
      ...workspace,
      widgets: workspace.widgets.map((widget) => ({
        ...widget,
        id: remapWidgetId(widget.id),
      })),
      placements: workspace.placements.map((placement) => ({
        ...placement,
        widgetId: remapWidgetId(placement.widgetId),
      })),
      widgetSettings: remapWidgetSettings(
        workspace.widgetSettings,
        remapWidgetId,
      ),
    },
    settings: source.settings,
    templateId: source.templateId,
  });
};

export const createWorkspaceDocument = (
  workspaces: WorkspaceEntry[],
  revision = 0,
): WorkspaceDocument => ({
  schemaVersion: 3,
  revision,
  engine: "rgl",
  workspaces: structuredClone(workspaces),
});

export const createDefaultWorkspaceDocument = (
  revision = 0,
): WorkspaceDocument => {
  const template = structuredClone(DEFAULT_LAYOUT_TEMPLATE);
  const workspaces = template.workspaces.map((entry) =>
    instantiateWorkspaceEntry(entry.name, entry.workspace, entry.settings),
  );
  return createWorkspaceDocument(workspaces, revision);
};

export const createInitialWorkspaceDocument = (
  revision = 0,
): WorkspaceDocument => createWorkspaceDocument([], revision);

export const createBlankWorkspaceDocument = (revision = 0): WorkspaceDocument =>
  createWorkspaceDocument([createWorkspaceEntry("Home")], revision);

export const parseWorkspaceDocument = (
  value: unknown,
): WorkspaceDocument | undefined => {
  const parsed = workspaceDocumentSchema.safeParse(value);
  if (!parsed.success) {
    return undefined;
  }
  const ids = parsed.data.workspaces.map((entry) => entry.id);
  if (new Set(ids).size !== ids.length) {
    return undefined;
  }
  return parsed.data as WorkspaceDocument;
};

export const workspaceDocumentsEqual = (
  left: WorkspaceDocument,
  right: WorkspaceDocument,
): boolean => JSON.stringify(left) === JSON.stringify(right);

export const canonicalWorkspaceDocument = (
  left: WorkspaceDocument,
  right: WorkspaceDocument,
): WorkspaceDocument =>
  JSON.stringify(left) >= JSON.stringify(right) ? left : right;
